import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getClientLoginPath } from '@/config/tenant'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import { registarUsuario } from '@/api/usuariosApi'
import { markClienteSessionActive } from '@/utils/clienteAuth'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import { COUNTRY_OPTIONS, type CountryCode, buildPhoneForStorage, formatPhone } from '@/utils/phone'
import styles from './CadastroPage.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function CadastroPage() {
  const navigate = useNavigate()
  const { setUser } = useUser()
  const { toast } = useToast()
  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [telefonePais, setTelefonePais] = useState<CountryCode>('PT')
  const [email, setEmail] = useState('')
  const [codigoAcesso, setCodigoAcesso] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [enviando, setEnviando] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const next: Record<string, string> = {}

    if (!nome.trim()) next.nome = 'Indique como quer ser tratado(a) — pode ser o primeiro nome.'
    if (telefone.trim() && telefone.replace(/\D/g, '').length < 9) {
      next.telefone = 'Telefone incompleto. Confira o indicativo e os dígitos.'
    }
    if (!email.trim()) next.email = 'O e-mail é obrigatório: serve para entrar na app.'
    else if (!EMAIL_REGEX.test(email.trim())) next.email = 'Formato de e-mail inválido.'
    if (!codigoAcesso.trim()) next.codigoAcesso = 'Crie um código de acesso (mínimo 4 caracteres) para desbloquear a app.'
    else if (codigoAcesso.trim().length < 4) next.codigoAcesso = 'Use pelo menos 4 caracteres (letras, números ou símbolos).'

    setErros(next)
    if (Object.keys(next).length > 0) {
      const first = Object.values(next)[0]
      if (first) toast(first, 'error')
      return
    }

    setEnviando(true)
    setErros({})
    try {
      const result = await registarUsuario({
        nome: nome.trim(),
        telefone: telefone.trim() ? buildPhoneForStorage(telefone, telefonePais) : undefined,
        email: email.trim(),
        codigo_acesso: codigoAcesso.trim(),
      })
      markClienteSessionActive()
      setUser({
        id: result.id,
        nome: result.nome,
        telefone: result.telefone,
        email: result.email,
        avatar_url: result.avatar_url ?? null,
      })
      toast('Conta criada! Da próxima vez use o mesmo e-mail e o mesmo código para entrar.', 'success')
      navigate('/menu', { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar conta. Tente de novo.'
      setErros({ submit: msg })
      toast(msg, 'error')
    } finally {
      setEnviando(false)
    }
  }

  const clearErro = (field: string) => {
    setErros((prev) => {
      const u = { ...prev }
      delete u[field]
      return u
    })
  }

  const handleTelefoneChange = (value: string) => {
    const formatted = value ? formatPhone(value, telefonePais) : ''
    setTelefone(formatted)
    clearErro('telefone')
  }

  const handleTelefonePaisChange = (code: CountryCode) => {
    setTelefonePais(code)
    if (telefone.trim()) {
      setTelefone(formatPhone(telefone, code))
    }
    clearErro('telefone')
  }

  return (
    <main className={`standalone-page ${styles.page} animate-slide-up`}>
      <button type="button" className={styles.backLink} onClick={() => navigate(getClientLoginPath())}>
        ← Voltar ao login
      </button>

      <div className={styles.card}>
        <div className={styles.cardInner}>
          <h1 className={styles.heading}>Criar conta de cliente</h1>
          <p className={styles.subheading}>
            Com esta conta marca serviços, consulta o histórico e recebe lembretes. É um registo único: depois só
            precisa de <strong>e-mail + código</strong> para entrar.
          </p>

          <TenantIdentityCard className={styles.contextCard} />
          <p className={styles.contextNote}>
            A sua conta de cliente fica ligada a esta empresa. Se o nome não corresponder ao estabelecimento
            pretendido, use o link que o negócio lhe enviou.
          </p>

          <p className={styles.stepsIntro}>
            <strong>Dois passos simples:</strong> primeiro os seus dados de contacto; depois define como vai entrar na
            app (e-mail + código que só você conhece).
          </p>

          <form className={styles.form} onSubmit={handleSubmit}>
            <section className={styles.section} aria-labelledby="cad-step1">
              <div className={styles.sectionHead}>
                <span className={styles.stepNum} aria-hidden>
                  1
                </span>
                <div>
                  <h2 id="cad-step1" className={styles.sectionTitle}>
                    Sobre si
                  </h2>
                  <p className={styles.sectionDesc}>Nome para o agendamento e contacto opcional.</p>
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="nome">
                  Nome ou alcunha
                  <span className={styles.labelHint}>Como quer aparecer nas marcações e notificações</span>
                </label>
                <input
                  id="nome"
                  type="text"
                  className={`${styles.input} ${erros.nome ? styles.inputErro : ''}`}
                  placeholder="Ex.: Maria Silva"
                  value={nome}
                  onChange={(e) => {
                    setNome(e.target.value)
                    clearErro('nome')
                  }}
                  autoComplete="name"
                  aria-invalid={!!erros.nome}
                  aria-describedby={erros.nome ? 'erro-nome' : undefined}
                />
                {erros.nome && (
                  <span id="erro-nome" className={styles.erroCampo} role="alert">
                    {erros.nome}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="telefone">
                  Telemóvel <span className={styles.opcional}>(opcional)</span>
                  <span className={styles.labelHint}>Ajuda o salão a contactá-lo se houver alterações ao horário</span>
                </label>
                <div className={styles.phoneRow}>
                  <select
                    className={styles.phoneCountry}
                    value={telefonePais}
                    onChange={(e) => handleTelefonePaisChange(e.target.value as CountryCode)}
                    aria-label="Indicativo do país"
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    id="telefone"
                    type="tel"
                    className={`${styles.input} ${styles.phoneInput} ${erros.telefone ? styles.inputErro : ''}`}
                    placeholder={telefonePais === 'PT' ? '912 345 678' : '(11) 91234-5678'}
                    value={telefone}
                    onChange={(e) => handleTelefoneChange(e.target.value)}
                    autoComplete="tel"
                    aria-invalid={!!erros.telefone}
                    aria-describedby={erros.telefone ? 'erro-telefone' : undefined}
                  />
                </div>
                {erros.telefone && (
                  <span id="erro-telefone" className={styles.erroCampo} role="alert">
                    {erros.telefone}
                  </span>
                )}
              </div>
            </section>

            <section className={styles.section} aria-labelledby="cad-step2">
              <div className={styles.sectionHead}>
                <span className={styles.stepNum} aria-hidden>
                  2
                </span>
                <div>
                  <h2 id="cad-step2" className={styles.sectionTitle}>
                    Como vai entrar na app
                  </h2>
                  <p className={styles.sectionDesc}>
                    Não enviamos palavra-passe por e-mail: <strong>escolhe já</strong> um código que consiga memorizar
                    ou guardar com segurança.
                  </p>
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">
                  E-mail
                  <span className={styles.labelHint}>Será o seu «nome de utilizador» sempre que abrir a app</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={`${styles.input} ${erros.email ? styles.inputErro : ''}`}
                  placeholder="nome@exemplo.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    clearErro('email')
                  }}
                  autoComplete="email"
                  aria-invalid={!!erros.email}
                  aria-describedby={erros.email ? 'erro-email' : undefined}
                />
                {erros.email && (
                  <span id="erro-email" className={styles.erroCampo} role="alert">
                    {erros.email}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="codigoAcesso">
                  Código de acesso (sua palavra-passe)
                  <span className={styles.labelHint}>Mínimo 4 caracteres — só serve para esta app, com este e-mail</span>
                </label>
                <input
                  id="codigoAcesso"
                  type="password"
                  className={`${styles.input} ${erros.codigoAcesso ? styles.inputErro : ''}`}
                  placeholder="Ex.: uma palavra ou PIN que só você saiba"
                  value={codigoAcesso}
                  onChange={(e) => {
                    setCodigoAcesso(e.target.value)
                    clearErro('codigoAcesso')
                  }}
                  autoComplete="new-password"
                  aria-invalid={!!erros.codigoAcesso}
                  aria-describedby={erros.codigoAcesso ? 'hint-codigo erro-codigo' : 'hint-codigo'}
                />
                <div id="hint-codigo" className={styles.hintBox}>
                  <strong>O que é isto?</strong> Funciona como a senha do banco, mas definida por si. Na página inicial
                  da app escolhe «Entrar», cola o e-mail e este mesmo código — não precisa de SMS nem de «repor
                  password» por e-mail.
                </div>
                {erros.codigoAcesso && (
                  <span id="erro-codigo" className={styles.erroCampo} role="alert">
                    {erros.codigoAcesso}
                  </span>
                )}
              </div>
            </section>

            {erros.submit && (
              <p className={styles.erroCampo} style={{ marginBottom: 14 }} role="alert">
                {erros.submit}
              </p>
            )}

            <button type="submit" className={styles.btnCriar} disabled={enviando}>
              {enviando ? 'A criar a sua conta…' : 'Criar conta e continuar'}
            </button>

            <p className={styles.footerNote}>
              Ao continuar, confirma que os dados estão correctos. Em caso de dúvida, peça ajuda ao{' '}
              <strong>estabelecimento</strong> — eles sabem qual é o link e a conta correctos para clientes.
            </p>
          </form>
        </div>
      </div>
    </main>
  )
}
